/**
 * MarketplaceNeedsSetupPage — /marketplace/needs-setup
 * Acquired entities waiting on an MCP connection, in a table with a bind control. Demo pages: /connected-apps (rows
 * with a status badge and a connect / disconnect button) and /tables/data-tables. Template example: NextAdmin
 * `(dashboard)/(home)/_component/last-transactions-table`. Binding and unbinding are `CanManageIntegrations`.
 *
 * "Credentials never transfer": an acquired entity that reaches an MCP tool, directly or through what it links,
 * refuses to run until the holder binds one of its own MCP connections. Bound rows are not re-listed by the server
 * (once bound they no longer need setup), so a just-bound row is kept in `boundLocally` with its own Unbind action
 * instead of disappearing mid-review.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, PlugZap, Unplug } from 'lucide-react';
import {
  bindMarketplaceEntityConnectionMutation,
  listMarketplaceNeedsSetupOptions,
  listMarketplaceNeedsSetupQueryKey,
  listMcpConnectionsOptions,
  unbindMarketplaceEntityConnectionMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { Entity } from '../../generated/api/types.gen.js';
import { buttonStyles } from '../../components/tailgrids/core/button.js';
import { ListPage } from '../../components/page/index.js';
import { Can } from '../../components/auth/Can.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Callout } from '../../components/ui/Callout.js';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { Select } from '../../components/ui/Input.js';
import { useToast } from '../../components/ui/Toast.js';
import { PAGE_HELP } from '../../help/index.js';
import { marketplaceEntityLabel } from './marketplaceLabels.js';

type Row =
  | { kind: 'waiting'; entity: Entity; reason: 'direct' | 'dependency' }
  | { kind: 'bound'; entity: Entity; connectionId: number };

export const MarketplaceNeedsSetupPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const [pickerFor, setPickerFor] = useState<number | null>(null);
  const [boundLocally, setBoundLocally] = useState<Map<number, { entity: Entity; connectionId: number }>>(new Map());

  const query = useQuery(listMarketplaceNeedsSetupOptions());
  const connectionsQuery = useQuery(listMcpConnectionsOptions());
  const connections = connectionsQuery.data?.data ?? [];

  const items = (query.data?.data ?? []).filter((item) => !boundLocally.has(item.entity.id));
  const refresh = () => queryClient.invalidateQueries({ queryKey: listMarketplaceNeedsSetupQueryKey() });

  const bind = useMutation({
    ...bindMarketplaceEntityConnectionMutation(),
    onSuccess: (_result, variables) => {
      const entityId = variables.path?.entityId;
      if (entityId == null) return;
      const entity = items.find((item) => item.entity.id === entityId)?.entity;
      if (entity && variables.body) {
        const connectionId = variables.body.mcpConnectionId;
        setBoundLocally((previous) => new Map(previous).set(entityId, { entity, connectionId }));
      }
      setPickerFor(null);
      refresh();
    },
    onError: (error) => notify(error.message, 'danger'),
  });
  const unbind = useMutation({
    ...unbindMarketplaceEntityConnectionMutation(),
    onSuccess: (_result, variables) => {
      const entityId = variables.path?.entityId;
      if (entityId == null) return;
      setBoundLocally((previous) => {
        const next = new Map(previous);
        next.delete(entityId);
        return next;
      });
      refresh();
    },
    onError: (error) => notify(error.message, 'danger'),
  });

  const rows: Row[] = [
    ...items.map((item): Row => ({ kind: 'waiting', entity: item.entity, reason: item.reason })),
    ...Array.from(boundLocally.values()).map(
      ({ entity, connectionId }): Row => ({ kind: 'bound', entity, connectionId }),
    ),
  ];
  const help = PAGE_HELP['marketplace-needs-setup'];

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'entity',
      header: 'Entity',
      width: '30%',
      render: ({ entity }) => (
        <div>
          <p className="text-[13px] font-semibold text-heading">{entity.name ?? entity.slug ?? 'Unnamed entity'}</p>
          {entity.description && <p className="mt-[3px] line-clamp-1 text-[11px] text-muted">{entity.description}</p>}
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: ({ entity }) => <Badge variant="neutral">{marketplaceEntityLabel(entity.type)}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => {
        if (row.kind === 'bound') {
          const name = connections.find((c) => c.id === row.connectionId)?.name ?? 'a connection';
          return <Badge variant="success">Bound to {name}</Badge>;
        }
        return row.reason === 'direct' ? (
          <Badge variant="warning">Uses an MCP tool</Badge>
        ) : (
          <Badge variant="neutral">Depends on one that does</Badge>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '300px',
      render: (row) => (
        <Can policy="CanManageIntegrations">
          {row.kind === 'bound' ? (
            <Button
              variant="secondary"
              size="sm"
              disabled={unbind.isPending}
              onClick={() => unbind.mutate({ path: { entityId: row.entity.id } })}
            >
              <Unplug size={14} aria-hidden="true" />
              Unbind
            </Button>
          ) : pickerFor === row.entity.id ? (
            <div className="flex items-center justify-end gap-2">
              <Select
                aria-label={`Connection for ${row.entity.name ?? 'this entity'}`}
                className="w-48"
                defaultValue=""
                disabled={bind.isPending}
                onChange={(event) => {
                  const mcpConnectionId = Number(event.target.value);
                  if (mcpConnectionId) bind.mutate({ path: { entityId: row.entity.id }, body: { mcpConnectionId } });
                }}
              >
                <option value="" disabled>
                  Choose a connection…
                </option>
                {connections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
              <Button variant="secondary" size="sm" onClick={() => setPickerFor(null)}>
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              size="sm"
              disabled={connections.length === 0}
              title={connections.length === 0 ? 'No MCP connections in this organization yet' : undefined}
              onClick={() => setPickerFor(row.entity.id)}
            >
              <PlugZap size={14} aria-hidden="true" />
              Bind connection
            </Button>
          )}
        </Can>
      ),
    },
  ];

  return (
    <ListPage
      title="Needs setup"
      subtitle="Acquired entities that reach an MCP tool and are waiting on a connection before they can run."
      helpContent={help}
      breadcrumbs={[{ label: 'Marketplace', to: '/marketplace' }]}
      actions={
        <Link to="/marketplace" className={buttonStyles({ variant: 'primary', appearance: 'outline', size: 'sm' })}>
          Back to marketplace
        </Link>
      }
      alert={
        query.isError ? (
          <Callout
            tone="danger"
            icon={AlertTriangle}
            title="Could not load the entities that need setup"
            actions={
              <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          >
            {query.error.message}
          </Callout>
        ) : undefined
      }
    >
      {!query.isError && (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(row) => row.entity.id}
          loading={query.isPending}
          aria-label="Entities that need setup"
          empty={
            <EmptyState
              icon={PlugZap}
              title={help?.emptyState.title ?? 'Nothing needs setup'}
              description={
                help?.emptyState.description ??
                'Every entity this organization holds that reaches an MCP tool has a connection bound.'
              }
              learnItems={help?.emptyState.learnItems}
            />
          }
        />
      )}
    </ListPage>
  );
};

export default MarketplaceNeedsSetupPage;
